import { InputField } from 'fields/base/InputField/InputField';
import { MultiSelectField } from 'fields/MultiSelectField';
import React, { useMemo } from 'react';

import { useTypedSelector } from 'store';
import { Button } from 'ui/Button/Button';
import { ButtonLoading } from 'ui/Button/ButtonLoading';
import { ErrorText } from 'ui/ErrorText/ErrorText';
import { ProjectForm } from 'ui/forms';
import { Modal } from 'ui/Modal/Modal';

import { IUser } from './useProjectModal/models';
import { useProjectModal } from './useProjectModal/useProjectModal';

interface IProjectModalProps {
  type: 'create' | 'update';
  onClose: () => void;
  onOpen: () => void;
  projectId?: number;
  initialTitle: string | undefined;
  initialNotes: string | undefined;
  initialUsers: { id: number }[];
}

const ProjectModal: React.FC<IProjectModalProps> = ({
  type,
  onClose,
  onOpen,
  projectId,
  initialTitle,
  initialNotes,
  initialUsers,
}) => {
  const archivedProjects = useTypedSelector(store => store.projects.archivedProjects);

  const isProjectArchived = useMemo(
    () => archivedProjects?.find(el => el.id === projectId),
    [archivedProjects, projectId],
  );

  const {
    loading,
    values,
    handleSubmit,
    touched,
    errors,
    setFieldValue,
    onDeleteProject,
    onArchiveProject,
  } = useProjectModal({
    type,
    onClose,
    onOpen,
    projectId,
    title: initialTitle,
    notes: initialNotes,
    users: initialUsers,
  });

  const projectModalTitle = useMemo(
    () => (type === 'create' ? 'Create new project' : 'Change project settings'),
    [type],
  );

  let error: string | undefined = undefined;
  if (errors.title && touched.title) {
    error = errors.title;
  } else if (errors.notes && touched.notes) {
    error = errors.notes;
  }

  const onSelectUsers = (newSelectedUsers: IUser[]) => {
    const newUsers = values.users.map(el => ({
      ...el,
      selected: !!newSelectedUsers.find(_el => _el.id === el.id),
    }));

    return setFieldValue('users', newUsers);
  };

  return (
    <Modal title={projectModalTitle} onClose={onClose}>
      <ProjectForm>
        <InputField
          label="Name"
          type="text"
          value={values.title ?? ''}
          disabled={loading}
          setValue={newTitle => setFieldValue('title', newTitle)}
        />
        <MultiSelectField
          label="Users"
          placeholder="Add users"
          items={values.users}
          itemToString={user => user.login}
          onSelectItems={onSelectUsers}
          selectedItems={values.users.filter(el => !!el.selected)}
          disabled={loading}
        />
        <InputField
          label="Notes"
          type="text"
          value={values.notes ?? ''}
          disabled={loading}
          setValue={newNotes => setFieldValue('notes', newNotes)}
        />
        <ErrorText>{error}</ErrorText>
        <div className="mt-2">
          {type === 'create' && (
            <>
              {!loading && (
                <Button type="button" onClick={() => handleSubmit()}>
                  Create
                </Button>
              )}
              {loading && <ButtonLoading />}
            </>
          )}
          {type === 'update' && (
            <>
              {!loading && (
                <div className="w-full flex flex-col gap-2">
                  <Button type="button" onClick={() => handleSubmit()}>
                    Confirm changing
                  </Button>
                  {!isProjectArchived && (
                    <Button color="secondary" type="button" onClick={onArchiveProject}>
                      Archive project
                    </Button>
                  )}
                  <Button color="error-secondary" type="button" onClick={onDeleteProject}>
                    Delete project
                  </Button>
                </div>
              )}
              {loading && <ButtonLoading />}
            </>
          )}
        </div>
      </ProjectForm>
    </Modal>
  );
};

export { ProjectModal };
